import React, { useCallback, useEffect, useRef, useState } from 'react';
import './SesKaydiPlayerModal.css';

const formatAudioTime = (sec) => {
  if (!Number.isFinite(sec) || sec < 0) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
};

/** 10 sn atlama ikonu — SVG ile ortalanmış rakam */
const Skip10Icon = ({ yon = 'geri' }) => {
  const ileri = yon === 'ileri';
  return (
    <svg
      className="ses-kaydi-player__skip-svg"
      viewBox="0 0 36 36"
      width="36"
      height="36"
      aria-hidden="true"
    >
      <g transform={ileri ? 'translate(36, 0) scale(-1, 1)' : undefined}>
        <path
          d="M25.5 12.5 A 10 10 0 1 0 10.5 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
        />
        <path
          d="M10.5 24 L8 21.5 M10.5 24 L13 21.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <text
        x="18"
        y="18.5"
        textAnchor="middle"
        dominantBaseline="middle"
        fontSize="10"
        fontWeight="700"
        fill="currentColor"
        fontFamily="system-ui, -apple-system, 'Segoe UI', sans-serif"
      >
        10
      </text>
    </svg>
  );
};

const SesKaydiPlayerModal = ({ kayit, onClose }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const src = kayit?.sesKaydi || '';

  const stopPlayback = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.pause();
    audio.currentTime = 0;
    setPlaying(false);
    setCurrentTime(0);
  }, []);

  useEffect(() => {
    stopPlayback();
    setLoading(true);
    setError('');
    setDuration(0);
  }, [src, stopPlayback]);

  useEffect(() => () => stopPlayback(), [stopPlayback]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio || error) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      audio.play().catch(() => {
        setError('Ses kaydı oynatılamadı.');
        setPlaying(false);
      });
      setPlaying(true);
    }
  };

  const skip = (delta) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    audio.currentTime = Math.min(duration, Math.max(0, audio.currentTime + delta));
    setCurrentTime(audio.currentTime);
  };

  const handleTimeUpdate = () => {
    const audio = audioRef.current;
    if (audio) setCurrentTime(audio.currentTime);
  };

  const handleLoadedMetadata = () => {
    const audio = audioRef.current;
    if (audio) {
      setDuration(audio.duration || 0);
      setLoading(false);
    }
  };

  const handleEnded = () => {
    setPlaying(false);
    setCurrentTime(0);
  };

  const handleSeek = (e) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    audio.currentTime = ratio * duration;
    setCurrentTime(audio.currentTime);
  };

  const handleVolumeChange = (e) => {
    const v = Number(e.target.value);
    setVolume(v);
    setMuted(v === 0);
    if (audioRef.current) {
      audioRef.current.volume = v;
      audioRef.current.muted = v === 0;
    }
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    if (!audio) return;
    const next = !muted;
    setMuted(next);
    audio.muted = next;
    if (!next && volume === 0) {
      const v = 0.8;
      setVolume(v);
      audio.volume = v;
    }
  };

  if (!kayit?.sesKaydi) return null;

  const progressPct = duration > 0 ? (currentTime / duration) * 100 : 0;
  const baslik = kayit.musteriAdi || 'Bilinmeyen müşteri';
  const telefon = kayit.telefon || kayit.arayan || '—';
  const cevaplandi = kayit.yon === 1;

  return (
    <div
      className="modal-overlay ses-kaydi-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ses-kaydi-modal-title"
      onClick={onClose}
    >
      <div className="ses-kaydi-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ses-kaydi-modal__hero">
          <button type="button" className="ses-kaydi-modal__close" onClick={onClose} aria-label="Kapat">
            <i className="fas fa-times" aria-hidden />
          </button>

          <div className="ses-kaydi-modal__avatar" aria-hidden>
            <i className="fas fa-phone-volume" />
          </div>

          <h2 id="ses-kaydi-modal-title" className="ses-kaydi-modal__title">{baslik}</h2>
          <p className="ses-kaydi-modal__phone">{telefon}</p>

          <div className="ses-kaydi-modal__chips">
            {kayit.tarihMetin && (
              <span className="ses-kaydi-modal__chip">
                <i className="fas fa-calendar-alt" aria-hidden /> {kayit.tarihMetin}
              </span>
            )}
            <span className={`ses-kaydi-modal__chip ses-kaydi-modal__chip--${cevaplandi ? 'ok' : 'miss'}`}>
              <i className={`fas fa-${cevaplandi ? 'check-circle' : 'phone-slash'}`} aria-hidden />
              {kayit.durum || (cevaplandi ? 'Cevaplandı' : 'Cevapsız')}
            </span>
            {kayit.mesaiDisi != null && (
              <span className={`ses-kaydi-modal__chip ses-kaydi-modal__chip--${kayit.mesaiDisi ? 'off' : 'on'}`}>
                <i className="fas fa-clock" aria-hidden />
                Mesai {kayit.mesaiDisi ? 'dışı' : 'içi'}
              </span>
            )}
            {kayit.sureMetin && (
              <span className="ses-kaydi-modal__chip">
                <i className="fas fa-hourglass-half" aria-hidden /> {kayit.sureMetin}
              </span>
            )}
          </div>
        </div>

        <div className="ses-kaydi-modal__body">
          {error && (
            <div className="ses-kaydi-modal__error">
              <i className="fas fa-exclamation-circle" aria-hidden /> {error}
            </div>
          )}

          <div className={`ses-kaydi-player${playing ? ' is-playing' : ''}`}>
            <audio
              ref={audioRef}
              src={src}
              preload="metadata"
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              onCanPlay={() => setLoading(false)}
              onEnded={handleEnded}
              onError={() => {
                setLoading(false);
                setError('Ses kaydı yüklenemedi.');
                setPlaying(false);
              }}
            />

            <div className="ses-kaydi-player__viz" aria-hidden>
              {[...Array(24)].map((_, i) => (
                <span key={i} className="ses-kaydi-player__bar" style={{ '--i': i }} />
              ))}
            </div>

            <div className="ses-kaydi-player__main">
              <button
                type="button"
                className="ses-kaydi-player__skip"
                onClick={() => skip(-10)}
                disabled={loading || !!error || !duration}
                aria-label="10 saniye geri"
              >
                <Skip10Icon yon="geri" />
              </button>

              <button
                type="button"
                className="ses-kaydi-player__play-btn"
                onClick={togglePlay}
                disabled={loading || !!error}
                aria-label={playing ? 'Duraklat' : 'Oynat'}
              >
                <span className="ses-kaydi-player__play-ring" aria-hidden />
                {loading ? (
                  <i className="fas fa-spinner fa-spin" aria-hidden />
                ) : (
                  <i className={`fas fa-${playing ? 'pause' : 'play'}`} aria-hidden />
                )}
              </button>

              <button
                type="button"
                className="ses-kaydi-player__skip"
                onClick={() => skip(10)}
                disabled={loading || !!error || !duration}
                aria-label="10 saniye ileri"
              >
                <Skip10Icon yon="ileri" />
              </button>
            </div>

            <div className="ses-kaydi-player__timeline-row">
              <span className="ses-kaydi-player__time">{formatAudioTime(currentTime)}</span>
              <div
                className="ses-kaydi-player__timeline"
                role="slider"
                aria-valuemin={0}
                aria-valuemax={duration || 0}
                aria-valuenow={currentTime}
                aria-label="Oynatma konumu"
                onClick={handleSeek}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowRight') skip(5);
                  else if (e.key === 'ArrowLeft') skip(-5);
                }}
                tabIndex={0}
              >
                <div className="ses-kaydi-player__track">
                  <div className="ses-kaydi-player__progress" style={{ width: `${progressPct}%` }} />
                  <div className="ses-kaydi-player__thumb" style={{ left: `${progressPct}%` }} />
                </div>
              </div>
              <span className="ses-kaydi-player__time">{formatAudioTime(duration)}</span>
            </div>

            <div className="ses-kaydi-player__volume-row">
              <button
                type="button"
                className="ses-kaydi-player__mute-btn"
                onClick={toggleMute}
                aria-label={muted ? 'Sesi aç' : 'Sessize al'}
              >
                <i className={`fas fa-volume-${muted || volume === 0 ? 'xmark' : volume < 0.5 ? 'low' : 'high'}`} aria-hidden />
              </button>
              <input
                type="range"
                className="ses-kaydi-player__volume"
                min="0"
                max="1"
                step="0.05"
                value={muted ? 0 : volume}
                onChange={handleVolumeChange}
                aria-label="Ses seviyesi"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SesKaydiPlayerModal;
